import React, { useCallback, useRef, useState } from "react";
import { Flex, Text, Box } from "theme-ui";
import { LiquityStoreState } from "@liquity/lib-base";
import { useLiquitySelector } from "@liquity/lib-react";
import useChain from "../hooks/useChain";
import { Icon } from "./Icon";
import { useWeb3React } from "@web3-react/core";
import { Web3Provider } from "@ethersproject/providers";
import { CHAINS, ChainId, SUPPORTED_CHAIN_IDS } from "../config/web3/chains";
import NetworkIcon from "./NetworkIcon";
import useOnClickOutside from "../hooks/useOnClickOutside";
import styled, { css } from "styled-components";

const select = ({ price }: LiquityStoreState) => ({
  price
});

const NetworkName = styled(Text).attrs(() => ({
  variant: "subtitle1"
}))`
  font-weight: 600;
  color: #804c3b;
  font-size: 14px;
  letter-spacing: 0.5px;
`;

type NetworkItemProps = {
  selected: boolean;
};

const NetworkItem = styled.div<NetworkItemProps>`
  ${({ theme, selected }) => css`
    display: flex;
    align-items: center;
    border-radius: 8px;
    padding: 12px;
    cursor: pointer;
    color: #804c3b;
    transition: all 0.2s ease;

    &.bsc:hover {
      img {
        filter: brightness(0.2);
      }
    }

    &:hover {
      background: #FEF3C7;
      ${NetworkName} {
        color: #000;
      }
    }

    ${selected &&
    css`
      cursor: not-allowed;
      pointer-events: none;
      background: #f0b90b;
      ${NetworkName} {
        color: #000;
        font-weight: 700;
      }

      &.bsc {
        img {
          filter: brightness(0.2);
        }
      }
    `}
  `}
`;

const NetworkItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
`;

export const PriceFeed: React.FC = () => {
  const { price } = useLiquitySelector(select);
  const chain = useChain();
  const currencySymbol = chain.nativeCurrency.symbol;
  const { library, chainId: currentChainId } = useWeb3React<Web3Provider>();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useOnClickOutside(ref, () => setIsOpen(false));

  const switchNetwork = useCallback(
    async (chainId: ChainId) => {
      if (library && library.provider && library.provider.request) {
        try {
          await library.provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: `0x${chainId.toString(16)}` }]
          });
        } catch (e: any) {
          if (e.code === 4902) {
            try {
              await library.provider.request({
                method: "wallet_addEthereumChain",
                params: [CHAINS[chainId].getAddEthereumChainParameters()]
              });
            } catch (addError) {
              console.error(addError);
            }
          }
          console.error(e);
        }
      }
      setIsOpen(false);
    },
    [library]
  );

  return (
    <Box sx={{ position: "relative" }}>
      <Flex
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          alignItems: "center",
          gap: 2,
          p: "8px 12px",
          backgroundColor: "white",
          border: "1px solid #E5E7EB",
          borderRadius: "8px",
          transition: "all 0.2s ease",
          cursor: "pointer",
          ":hover": {
            borderColor: "#f0b90b",
            boxShadow: "0 2px 8px rgba(240, 185, 11, 0.2)"
          },
          "@media screen and (max-width: 47.9375em)": {
            display: "none"
          }
        }}
      >
        <NetworkIcon iconPath={chain.networkIcon} />
        <Text sx={{ fontSize: 1, fontWeight: "600", color: "#111827", whiteSpace: "nowrap" }}>
          {currencySymbol} ${price.prettify(2)}
        </Text>
        <Icon name="angle-down" size="xs" />
      </Flex>

      {isOpen && (
        <Box
          ref={ref}
          sx={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            minWidth: "220px",
            backgroundColor: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            zIndex: 1000
          }}
        >
          <NetworkItems>
            {SUPPORTED_CHAIN_IDS.map(chainId => {
              const { name, networkIcon } = CHAINS[chainId];

              return (
                <NetworkItem
                  key={chainId}
                  className={
                    chainId === ChainId.BSC_MAINNET || chainId === ChainId.BSC_TESTNET ? "bsc" : ""
                  }
                  onClick={() => switchNetwork(chainId)}
                  selected={(currentChainId as ChainId) === chainId}
                >
                  <NetworkIcon iconPath={networkIcon} margin="0 12px 0 0" size={24} />
                  <NetworkName as="span">{name}</NetworkName>
                </NetworkItem>
              );
            })}
          </NetworkItems>
        </Box>
      )}
    </Box>
  );
};

